// This is the "old" barcode scanner, which at this point is always marked legacy

import { type LoadedRow, isLoadingValue } from "@glide/computation-model-types";
import { type ActionDescription, type PropertyDescription, ActionKind } from "@glide/app-description";
import { sheetNameForTable } from "@glide/type-schema";
import { makeRowID } from "@glide/common-core/dist/js/make-row-id";
import { type CodeScannerStandards } from "@glide/component-utils";
import {
    type AppDescriptionContext,
    type PropertyDescriptor,
    ColumnPropertyFlag,
    ColumnPropertyHandler,
    PropertySection,
    codeScannerScreenName,
    liveLinearBarcodeScannerScreenName,
    getPrimitiveNonHiddenColumnsSpec,
    type ActionAvailability,
} from "@glide/function-utils";
import {
    type WireActionHydrator,
    type WireActionInflationBackend,
    type WireActionResult,
    type WireActionResultBuilder,
    PageScreenTarget,
} from "@glide/wire";
import { type ActionDescriptor, ActionGroup } from "./action-descriptor";
import { type DescriptionToken, actionAvailabilityApps } from "./action-handler";
import { BaseActionHandler, tokenForProperty } from "./base";
import { type GlideIconProps, type PluginTierList, makeTierList } from "@glide/plugins";
import type { StaticActionContext } from "../static-context";
import { ICON_PALE } from "../plugins/icon-colors";

interface ScanCodeConfigDescription {
    readonly useExtendedEAN: PropertyDescription;
    readonly scanForQrCode: PropertyDescription;
}

interface ScanCodeActionDescription extends ActionDescription, ScanCodeConfigDescription {
    readonly output: PropertyDescription;
}

const columnPropertyHandler = new ColumnPropertyHandler(
    "output",
    "Column",
    [
        ColumnPropertyFlag.Editable,
        ColumnPropertyFlag.Required,
        ColumnPropertyFlag.EditedInApp,
        ColumnPropertyFlag.AllowUserProfileColumns,
    ],
    undefined,
    ["qrcode", "barcode", "sku"],
    getPrimitiveNonHiddenColumnsSpec,
    "string",
    PropertySection.Data
);

export function inflateScanBarcodeAction(
    ib: WireActionInflationBackend,
    columnProperty: PropertyDescription,
    standards: CodeScannerStandards | undefined,
    arb: WireActionResultBuilder
): WireActionHydrator | WireActionResult {
    arb = arb.addData({ tableName: sheetNameForTable(ib.tables.output), standards });

    const { tableAndColumn, setterMaker } = ib.getValueSetterForProperty(columnProperty, "set-barcode");
    if (tableAndColumn === undefined) return arb.inflationError("Invalid column");

    const isMutatingScreen = ib.mutatingScreenKind !== undefined;

    return (vp, skipLoading) => {
        const setter = setterMaker(vp);
        if (isLoadingValue(setter)) return arb.errorIfSkipLoading(skipLoading, "Destination column");
        if (setter === undefined) return arb.error(true, "Cannot write to column");

        const row: LoadedRow = {
            $rowID: makeRowID(),
            $isVisible: false,
            standards,
            acceptOnFirst: isMutatingScreen,
            // The scanner page will set this to the scanned value if the
            // scan was successful.
            value: undefined,
        };

        return ab => {
            return new Promise(resolve => {
                try {
                    ab.addSpecialScreenRow(row);
                    ab.pushFreeScreen(
                        standards !== undefined ? codeScannerScreenName : liveLinearBarcodeScannerScreenName,
                        [row],
                        "",
                        PageScreenTarget.SmallModal,
                        undefined,
                        async () => {
                            try {
                                if (row.value === undefined) return resolve(arb.error(true, "No value for barcode"));
                                const result = await setter(ab, row.value);
                                return resolve(arb.fromResult(result));
                            } catch (e: unknown) {
                                return resolve(arb.fromException(true, e));
                            }
                        }
                    );
                } catch (e: unknown) {
                    return resolve(arb.fromException(true, e));
                }
            });
        };
    };
}

export class ScanCodeActionHandler extends BaseActionHandler<ScanCodeActionDescription> {
    public readonly kind = ActionKind.ScanCode;

    public readonly iconName: GlideIconProps = {
        icon: "st-scan-barcode",
        kind: "stroke",
        strokeFgColor: ICON_PALE,
    };

    public readonly name: string = "Scan barcode or QR code";

    public get handlesSequencing(): boolean {
        return true;
    }

    public get availability(): ActionAvailability {
        return actionAvailabilityApps;
    }

    public getTier(): PluginTierList | undefined {
        return makeTierList("business");
    }

    public getDescriptor(): ActionDescriptor {
        const properties: PropertyDescriptor[] = [columnPropertyHandler];

        return {
            name: this.name,
            group: ActionGroup.Interaction,
            groupItemOrder: 16,
            needsScreenContext: true,
            isLegacy: true,
            properties,
        };
    }

    public getTokenizedDescription(
        desc: ScanCodeActionDescription,
        env: StaticActionContext<AppDescriptionContext>
    ): readonly DescriptionToken[] | undefined {
        const token = tokenForProperty(desc.output, env);
        if (token === undefined) return undefined;
        return [{ kind: "string", value: "into " }, token];
    }

    public inflate(
        ib: WireActionInflationBackend,
        desc: ScanCodeActionDescription,
        arb: WireActionResultBuilder
    ): WireActionHydrator | WireActionResult {
        return inflateScanBarcodeAction(ib, desc.output, undefined, arb);
    }
}
